import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { useGetSsoAuthUrl } from 'utils/customHooks/api/useGetSsoAuthUrl';
import { getWindowLocation } from 'utils/window';
import { DynamicHelium } from 'utils/helium';
import { useOpMutation } from 'utils/customHooks/useOpMutation';
import { getHeliumUrlForEnv } from 'constants/envs';

import { useAltaLoginUrlParams } from '../../hooks/useAltaLoginUrlParams';
import { LoginFlow } from '../LoginFlow/LoginFlow';
import { ForgotPasswordFlow } from '../ForgotPasswordFlow/ForgotPasswordFlow';
import { ResetPasswordFlow } from '../ResetPasswordFlow/ResetPasswordFlow';
import { UseAltaIdButton } from '../UseAltaIdButton/UseAltaIdButton';
import { HandleIdpSsoLoginArgs } from '../../types/ssoLogin';

// Shared by every per-env SSO mutation below. Only the client differs, so the
// options live here rather than being restated once per env.
const SSO_AUTH_URL_MUTATION_OPTIONS = {
  apiEndpointName: 'getSsoAuthUrl',
  shouldSuppressErrorMessage: (error: OP.ApiError) =>
    error.statusCode === 401 && error.message === 'Namespace is required.',
} as const;

interface UnifiedLoginProps {
  redirectUrl?: string;
}

export const UnifiedLogin = ({ redirectUrl }: UnifiedLoginProps) => {
  const { t } = useTranslation();

  const {
    resetPassword,
    forgotPassword: forgotPasswordParam,
    emailParam,
  } = useAltaLoginUrlParams();
  const [forgotPassword, setForgotPassword] = useState<{
    didForget: boolean;
    initialEmail: string | null;
  }>({
    didForget: !!(forgotPasswordParam === 'true' && emailParam),
    initialEmail:
      forgotPasswordParam === 'true' && emailParam ? emailParam : null,
  });

  // Cross-region SSO lookup: each env is queried through its own Helium.
  // URLs come from constants/envs.ts so a new region needs no host wired up
  // here -- but it does still need its own hook and map entry below, because
  // hooks cannot be called in a loop.
  const prodHeliumClient = new DynamicHelium(
    getHeliumUrlForEnv('prod'),
  ).createClient();
  const prodeu1HeliumClient = new DynamicHelium(
    getHeliumUrlForEnv('prodeu1'),
  ).createClient();
  const prodcan1HeliumClient = new DynamicHelium(
    getHeliumUrlForEnv('prodcan1'),
  ).createClient();
  const prodaus1HeliumClient = new DynamicHelium(
    getHeliumUrlForEnv('prodaus1'),
  ).createClient();
  const devHeliumClient = new DynamicHelium(
    getHeliumUrlForEnv('dev'),
  ).createClient();
  const sandboxHeliumClient = new DynamicHelium(
    getHeliumUrlForEnv('sandbox'),
  ).createClient();

  const getSsoAuthUrlMutation = useGetSsoAuthUrl();

  const prodSsoAuthUrlMutation = useOpMutation({
    ...SSO_AUTH_URL_MUTATION_OPTIONS,
    overrideApiClient: prodHeliumClient,
  });
  const prodeu1SsoAuthUrlMutation = useOpMutation({
    ...SSO_AUTH_URL_MUTATION_OPTIONS,
    overrideApiClient: prodeu1HeliumClient,
  });
  const devSsoAuthUrlMutation = useOpMutation({
    ...SSO_AUTH_URL_MUTATION_OPTIONS,
    overrideApiClient: devHeliumClient,
  });

  const sandboxSsoAuthUrlMutation = useOpMutation({
    ...SSO_AUTH_URL_MUTATION_OPTIONS,
    overrideApiClient: sandboxHeliumClient,
  });

  const prodcan1SsoAuthUrlMutation = useOpMutation({
    ...SSO_AUTH_URL_MUTATION_OPTIONS,
    overrideApiClient: prodcan1HeliumClient,
  });

  const prodaus1SsoAuthUrlMutation = useOpMutation({
    ...SSO_AUTH_URL_MUTATION_OPTIONS,
    overrideApiClient: prodaus1HeliumClient,
  });

  // Indexed by OP.Env below, so omitting a new env is a compile error here
  // rather than a silently missing cross-region SSO path.
  const envSsoMutations = useMemo(
    () => ({
      dev: devSsoAuthUrlMutation,
      prod: prodSsoAuthUrlMutation,
      prodeu1: prodeu1SsoAuthUrlMutation,
      prodcan1: prodcan1SsoAuthUrlMutation,
      prodaus1: prodaus1SsoAuthUrlMutation,
      sandbox: sandboxSsoAuthUrlMutation,
      mine: getSsoAuthUrlMutation,
      test: getSsoAuthUrlMutation,
    }),
    [
      devSsoAuthUrlMutation,
      prodSsoAuthUrlMutation,
      prodeu1SsoAuthUrlMutation,
      prodcan1SsoAuthUrlMutation,
      prodaus1SsoAuthUrlMutation,
      sandboxSsoAuthUrlMutation,
      getSsoAuthUrlMutation,
    ],
  );

  // call a SSO login api for IDP
  const handleIdpSsoLogin = ({
    email,
    env,
    namespaceNickname,
    onError,
  }: HandleIdpSsoLoginArgs) => {
    // have a fallback which relies on current env variable for whatever reason which will generally be "mine" and "test"
    const mutation = env ? envSsoMutations[env] : getSsoAuthUrlMutation;

    mutation.mutate(
      {
        payload: {
          email,
          namespaceNickname,
          ...(redirectUrl && { redirectUrl }),
        },
      },
      {
        onSuccess: (data) => {
          getWindowLocation().href = data.authUrl!;
        },
        onError: (error) => {
          if (typeof onError === 'function') {
            onError(error);
          }
        },
      },
    );
  };

  const handleBackToSignIn = useCallback((initialEmail: string) => {
    setForgotPassword({ didForget: false, initialEmail });
  }, []);

  const showLogin = !resetPassword && !forgotPassword.didForget;
  const showForgotPassword = forgotPassword.didForget;
  const showResetPassword = !!resetPassword;
  let FormInputPortion = null;

  if (showLogin) {
    FormInputPortion = (
      <LoginFlow
        initialEmail={forgotPassword.initialEmail}
        onForgotPassword={setForgotPassword}
        handleIdpSsoLogin={handleIdpSsoLogin}
      />
    );
  }

  if (showForgotPassword) {
    FormInputPortion = (
      <ForgotPasswordFlow
        initialEmail={forgotPassword.initialEmail}
        autoSubmit={forgotPasswordParam === 'true' && !!emailParam}
        onBackToSignIn={handleBackToSignIn}
        handleIdpSsoLogin={handleIdpSsoLogin}
      />
    );
  }

  if (showResetPassword) {
    FormInputPortion = <ResetPasswordFlow backToSignin={handleBackToSignIn} />;
  }

  return (
    <>
      {FormInputPortion}

      <OpDivider plain>{t('or')}</OpDivider>
      <UseAltaIdButton />
    </>
  );
};
